import { captureException } from '@sentry/react';
import throttle from 'lodash.throttle';
import { RefObject } from 'react';
import invariant from 'tiny-invariant';
import { Machine, MachineConfig, assign } from 'xstate';

import { config as envConfig } from 'src/config';
import { Menu } from 'src/modules/designStudio/constants/menu';
import { client as apolloClient } from 'src/shared/graphql';
import { JobThreeDFiles } from 'src/shared/graphql/generated';
import { queries } from 'src/shared/graphql/requests';
import { emitPageAction, modelInteracted } from 'src/shared/telemetry';

import {
  ExtendedMacheteViewer,
  changeCameraFocus,
  getFocusOptionsForMenu,
  listenForEventFactory,
  renderMachete,
  updateStyles,
} from '../../helpers/machete';
import { sharedActions } from '../sharedActions';

export type Building = object | null;

const THROTTLE_TIME = 3000;

export interface Context {
  jobId: number | null;
  canvasRef: RefObject<HTMLElement> | null;
  building: Building;
  originalFeaturesStyles: object;
  viewer: ExtendedMacheteViewer | null;
  metadataUrl: string | undefined;
  geometryUrl: string | undefined;
}

export type Schema = {
  states: {
    fetchingUrls: {};
    fetchingInitialModelData: {};
    renderingModel: {};
    idle: {};
  };
};

export interface UpdateStylesEvent {
  type: 'UPDATE_STYLES';
  // not using this key
  data?: {};
  category: string;
  update: object;
}

interface SetBuildingAndViewerEvent {
  type: 'SET_BUILDING';
  data: {
    building: Building;
    viewer: ExtendedMacheteViewer;
    originalFeaturesStyles: object;
  };
}

interface RenderModelEvent {
  type: 'RENDER_MODEL';
  data: [Texture[], Geometry, Metadata];
}

interface FetchDataEvent {
  type: 'FETCH_DATA';
  data: {
    urls: {
      metadataUrl: string | undefined;
      geometryUrl: string | undefined;
    };
  };
}

export interface ChangeViewEvent {
  type: 'CHANGE_VIEW';
  menu: Menu | null;
  canvasHeight: string;
  windowTreatment?: string;
  autoFocusTreatment?: string;
}

export type Event =
  | UpdateStylesEvent
  | SetBuildingAndViewerEvent
  | RenderModelEvent
  | FetchDataEvent
  | ChangeViewEvent;

type Texture = object;
type Geometry = object;
type Metadata = object;

const PRODUCTS_ENDPOINT = envConfig.PRODUCTS_URI;

const getJob = async (id: number) => {
  const job = await queries.getJob(apolloClient, { id });
  const threeDFiles = job.data.jobs.results[0]?.threeDFiles || {};

  return threeDFiles;
};

const initialContext = {
  building: null,
  canvasRef: null,
  jobId: null,
  originalFeaturesStyles: {},
  viewer: null,
  geometryUrl: undefined,
  metadataUrl: undefined,
};

type BuildingInteraction = 'pan' | 'zoom' | 'rotate';

const buildInteractionListenerActivities = (
  interactionEvent: BuildingInteraction,
) => (ctx: Context) => {
  const trackModelInteracted = throttle(
    () =>
      ctx.jobId &&
      modelInteracted({
        interaction_type: interactionEvent,
        job_id: ctx.jobId,
      }),
    THROTTLE_TIME,
  );

  const removeListener = listenForEventFactory(ctx.viewer)(
    interactionEvent,
    trackModelInteracted,
  );

  return removeListener;
};

export const options = {
  services: {
    fetchUrls: async (context: Context) => {
      if (context.jobId === null) return null;

      if (context.geometryUrl && context.metadataUrl) {
        return {
          urls: {
            geometryUrl: context.geometryUrl,
            metadataUrl: context.metadataUrl,
          },
        };
      }

      const urls: JobThreeDFiles = await getJob(context.jobId);
      return { urls };
    },
    fetchData: async (_: Context, evt: Event) => {
      const {
        data: { urls },
      } = evt as FetchDataEvent;

      try {
        invariant(
          urls.metadataUrl,
          'Missing metadataUrl  - Model can not be fully loaded',
        );
        invariant(
          urls.geometryUrl,
          'Missing geometryUrl - Model can not be fully loaded',
        );

        if (urls.geometryUrl && urls.metadataUrl) {
          const data: [
            textures: any,
            geometry: any,
            metadata: any,
          ] = await Promise.all([
            fetch(PRODUCTS_ENDPOINT).then(res => res.json()),
            fetch(urls.geometryUrl).then(res => res.json()),
            fetch(urls.metadataUrl).then(res => res.json()),
          ]);

          return data;
        }
      } catch (error) {
        captureException(error);
      }

      return null;
    },
    renderModel: async (context: Context, event: Event) => {
      const { canvasRef } = context;
      const {
        data: [textures, geometry, metadata],
      } = event as RenderModelEvent;

      const { building, viewer, originalFeaturesStyles } = await renderMachete({
        canvasRef,
        geometry,
        metadata,
        textures,
      });

      return { building, viewer, originalFeaturesStyles };
    },
  },
  actions: {
    // log error/exception to Sentry
    logError: sharedActions.logError,
    setBuildingAndViewer: assign(
      (_: Context, event: Event) => (event as SetBuildingAndViewerEvent).data,
    ),
    sendBuildingLoadedEvent: () => {
      emitPageAction('BuildingLoaded');
    },
    resizeView: ({ viewer }: Context, event: Event) => {
      const { canvasHeight, windowTreatment } = event as ChangeViewEvent;

      if (!viewer) return;

      // TODO: once DS_windows flag is stable refactor this section to only do what is in this section
      if (windowTreatment === 'on') {
        const fullView = '100%';
        if (canvasHeight === fullView) {
          viewer.resize();
        } else {
          viewer.resize(300, 100);
        }
        // remove once feature flag is removed
      } else {
        viewer.resize();
      }
    },
    updateStyles: (ctx: Context, event: Event) => {
      const { category, update } = event as UpdateStylesEvent;
      void updateStyles({
        building: ctx.building,
        category,
        update,
        originalFeaturesStyles: ctx.originalFeaturesStyles,
      });
    },
    focusActiveMenu: async (ctx: Context, event: Event) => {
      const { building, viewer } = ctx;
      const {
        menu,
        autoFocusTreatment,
        windowTreatment,
      } = event as ChangeViewEvent;

      const ActiveFeatures = new Set([
        Menu.ROOF,
        Menu.TRIM,
        Menu.WALL,
        Menu.WINDOW,
      ]);

      if (
        autoFocusTreatment !== 'on' ||
        (menu === Menu.WINDOW && windowTreatment !== 'on') ||
        (menu !== null && !ActiveFeatures.has(menu))
      )
        return;

      const { focusElement, focusOptions } = await getFocusOptionsForMenu(
        building,
        viewer,
        menu,
      );
      const delay = 0;
      void changeCameraFocus(viewer, focusElement, focusOptions, delay);
    },
  },
  activities: {
    trackZoom: buildInteractionListenerActivities('zoom'),
    trackRotation: buildInteractionListenerActivities('rotate'),
    trackPan: buildInteractionListenerActivities('pan'),
  },
};

export const config: MachineConfig<Context, Schema, Event> = {
  id: 'building',
  initial: 'fetchingUrls',
  context: {
    ...initialContext,
  },
  states: {
    fetchingUrls: {
      invoke: {
        src: 'fetchUrls',
        onDone: 'fetchingInitialModelData',
        onError: {
          target: 'idle',
          actions: 'logError',
        },
      },
    },
    fetchingInitialModelData: {
      invoke: {
        src: 'fetchData',
        onDone: 'renderingModel',
        onError: {
          actions: 'logError',
        },
      },
    },
    renderingModel: {
      invoke: {
        src: 'renderModel',
        onDone: {
          target: 'idle',
          actions: ['setBuildingAndViewer', 'sendBuildingLoadedEvent'],
        },
        onError: {
          actions: 'logError',
        },
      },
    },
    idle: {
      activities: ['trackZoom', 'trackRotation', 'trackPan'],
      on: {
        UPDATE_STYLES: {
          actions: ['updateStyles'],
        },
        CHANGE_VIEW: {
          actions: ['resizeView', 'focusActiveMenu'],
        },
      },
    },
  },
};

const createMachine = () => Machine(config, options);

export { createMachine };
